import {
  Button,
  Container,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowRight,
  IconCheck,
  IconClockHour4,
  IconFileText,
  IconFlask2,
  IconLeaf,
  IconMessageCircle,
  IconMicroscope,
  IconNotebook,
  IconRosetteDiscountCheck,
  IconShieldCheck,
  IconStarFilled,
  IconUserCheck,
} from "@tabler/icons-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { buildWhatsAppUrl } from "../../../Services/contact.service";
import {
  getAllReviews,
  getReviewsByAssignmentSlug,
  getReviewSummary,
} from "../../../Services/review.service";
import { getAssignmentPageBySlug } from "../../../data/assignmentPages";
import { AssignmentPageData } from "../../../types/assignmentPage";
import Seo from "../../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../../seo/utils";
import { getAssignmentPagePath } from "../../../utils/routes";
import AssignmentFAQ from "../AssignmentFAQ";
import Breadcrumbs from "../Breadcrumbs";
import GetHelpForm from "../../Shared/GetHelpForm";
import ReviewsPreviewSection from "../../Reviews/ReviewsPreviewSection";
import "../BotanyAssignmentPage2.css";

type ScholarCard = {
  name: string;
  degree: string;
  initials: string;
  description: string;
  completedOrders: number;
  studentReviews: number;
  stars: number;
  accent:
    | "botany-v2-avatar-one"
    | "botany-v2-avatar-two"
    | "botany-v2-avatar-three"
    | "botany-v2-avatar-four";
};

type ReviewCard = {
  title: string;
  meta: string;
  quote: string;
  date: string;
};

type ContentSection = {
  title: string;
  paragraphs: string[];
  bullets: string[];
};

function titleCase(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getPageKeyword(page: AssignmentPageData) {
  return (page.title || page.subject || "")
    .replace(/\s+assignment help$/i, "")
    .trim();
}

function isCityStyleAssignmentPage(page: AssignmentPageData) {
  return /^assignment-help-/i.test(page.slug);
}

function getHeroTitle(page: AssignmentPageData) {
  return page.title;
}

function getUrgentHeading(page: AssignmentPageData) {
  if (isCityStyleAssignmentPage(page)) {
    return `Get Urgent Assignment Help in ${page.subject} for Tight Deadlines`;
  }

  return `Get Urgent ${page.title} for Tight Deadlines`;
}

function getWhatsAppMessage(page: AssignmentPageData) {
  const keyword = getPageKeyword(page);
  return `Hi, I need help with my ${keyword} assignment.`;
}

function getSupportTrack(page: AssignmentPageData) {
  const haystack =
    `${page.slug} ${page.subject} ${page.category}`.toLowerCase();

  if (haystack.includes("law") || haystack.includes("tort")) {
    return "law";
  }

  if (
    haystack.includes("ajax") ||
    haystack.includes("sql") ||
    haystack.includes("computer graphics") ||
    haystack.includes("anova") ||
    haystack.includes("excel") ||
    haystack.includes("business intelligence") ||
    haystack.includes("business analytics")
  ) {
    return "tech";
  }

  if (haystack.includes("nutrition") || haystack.includes("clinical")) {
    return "health";
  }

  if (isCityStyleAssignmentPage(page)) {
    return "general";
  }

  return "business";
}

function getScholarCards(page: AssignmentPageData): ScholarCard[] {
  const track = getSupportTrack(page);

  if (track === "law") {
    return [
      {
        name: "Emily Harper",
        degree: "LL.M. in Tort and Civil Liability",
        initials: "EH",
        description:
          "Helps students organise legal reasoning more clearly, especially when the assignment needs stronger application of cases, principles, and argument flow.",
        completedOrders: 121,
        studentReviews: 46,
        stars: 5,
        accent: "botany-v2-avatar-one",
      },
      {
        name: "Noah Kent",
        degree: "LL.B. and Legal Research Specialist",
        initials: "NK",
        description:
          "Strong at case-law structure, issue spotting, and turning scattered legal notes into more defensible written analysis.",
        completedOrders: 108,
        studentReviews: 40,
        stars: 4,
        accent: "botany-v2-avatar-two",
      },
      {
        name: "Sara Doyle",
        degree: "Ph.D. in Legal Theory",
        initials: "SD",
        description:
          "Best for coursework that needs clearer use of authorities, better counterargument handling, and more confident conclusion writing.",
        completedOrders: 129,
        studentReviews: 53,
        stars: 5,
        accent: "botany-v2-avatar-three",
      },
      {
        name: "Liam Frost",
        degree: "LL.M. in Comparative Law",
        initials: "LF",
        description:
          "Supports students with assignment planning, case comparison, and improving how legal arguments respond to the exact brief.",
        completedOrders: 97,
        studentReviews: 35,
        stars: 4,
        accent: "botany-v2-avatar-four",
      },
    ];
  }

  if (track === "tech") {
    return [
      {
        name: "Ryan Cole",
        degree: "MSc in Computer Science",
        initials: "RC",
        description:
          "Helps students explain technical choices more clearly, improve logic, and organise coding or systems assignments into cleaner academic structure.",
        completedOrders: 136,
        studentReviews: 51,
        stars: 5,
        accent: "botany-v2-avatar-one",
      },
      {
        name: "Mia Dawson",
        degree: "MTech in Data and Analytics",
        initials: "MD",
        description:
          "Best for analysis-heavy tasks that need clearer method choice, results explanation, and better connection between technical work and the brief.",
        completedOrders: 114,
        studentReviews: 43,
        stars: 4,
        accent: "botany-v2-avatar-two",
      },
      {
        name: "Adrian Wells",
        degree: "Ph.D. in Information Systems",
        initials: "AW",
        description:
          "Supports students with reports, visualisation, database logic, and software-related coursework where the explanation needs to be sharper.",
        completedOrders: 128,
        studentReviews: 55,
        stars: 5,
        accent: "botany-v2-avatar-three",
      },
      {
        name: "Sophie Bennett",
        degree: "MSc in Applied Computing",
        initials: "SB",
        description:
          "Useful for draft review, debugging direction, and turning rough technical work into more confident assignment-ready writing.",
        completedOrders: 101,
        studentReviews: 37,
        stars: 4,
        accent: "botany-v2-avatar-four",
      },
    ];
  }

  if (track === "health") {
    return [
      {
        name: "Olivia Hart",
        degree: "MSc in Clinical Nutrition",
        initials: "OH",
        description:
          "Supports students with evidence-based healthcare writing, clearer discussion of guidelines, and stronger explanation of clinical reasoning.",
        completedOrders: 117,
        studentReviews: 44,
        stars: 5,
        accent: "botany-v2-avatar-one",
      },
      {
        name: "James Parker",
        degree: "Ph.D. in Public Health",
        initials: "JP",
        description:
          "Best for case-based assignments that need stronger patient context, better use of evidence, and clearer recommendation structure.",
        completedOrders: 103,
        studentReviews: 39,
        stars: 4,
        accent: "botany-v2-avatar-two",
      },
      {
        name: "Leah Morgan",
        degree: "MSc in Nutrition Science",
        initials: "LM",
        description:
          "Helps students improve research use, paragraph clarity, and academic flow in nutrition essays, reports, and intervention plans.",
        completedOrders: 125,
        studentReviews: 52,
        stars: 5,
        accent: "botany-v2-avatar-three",
      },
      {
        name: "Daniel Reed",
        degree: "MPH in Health Analytics",
        initials: "DR",
        description:
          "Useful for assignments involving interpretation, comparison of evidence, and turning rough notes into more structured healthcare writing.",
        completedOrders: 94,
        studentReviews: 33,
        stars: 4,
        accent: "botany-v2-avatar-four",
      },
    ];
  }

  if (track === "general") {
    return [
      {
        name: "Rachel Morgan",
        degree: "Ph.D. in Academic Writing",
        initials: "RM",
        description:
          "Best for brief interpretation, deadline planning, and turning rough notes into a clearer submission path across different subjects.",
        completedOrders: 132,
        studentReviews: 54,
        stars: 5,
        accent: "botany-v2-avatar-one",
      },
      {
        name: "Daniel Price",
        degree: "MSc in Coursework Support",
        initials: "DP",
        description:
          "Helps students prioritise high-mark sections first and improve structure faster when the deadline is close.",
        completedOrders: 108,
        studentReviews: 41,
        stars: 4,
        accent: "botany-v2-avatar-two",
      },
      {
        name: "Leah Carter",
        degree: "MA in Research Support",
        initials: "LC",
        description:
          "Strong at citation clean-up, paragraph restructuring, and making draft feedback easier to act on before submission.",
        completedOrders: 121,
        studentReviews: 49,
        stars: 5,
        accent: "botany-v2-avatar-three",
      },
      {
        name: "Ethan Brooks",
        degree: "MTech in Technical and Mixed Coursework",
        initials: "EB",
        description:
          "Supports students who need clearer next steps across reports, essays, and technical tasks that still feel unfinished.",
        completedOrders: 97,
        studentReviews: 34,
        stars: 4,
        accent: "botany-v2-avatar-four",
      },
    ];
  }

  return [
    {
      name: "Alicia Warren",
      degree: "MBA in Strategy and Analysis",
      initials: "AW",
      description:
        "Helps students move from theory lists to clearer business arguments, especially when assignments need stronger analysis and recommendations.",
      completedOrders: 126,
      studentReviews: 48,
      stars: 5,
      accent: "botany-v2-avatar-one",
    },
    {
      name: "Marcus Hill",
      degree: "MSc in Management and Analytics",
      initials: "MH",
      description:
        "Best for coursework that needs better framework choice, cleaner structure, and more confident interpretation of business evidence.",
      completedOrders: 109,
      studentReviews: 42,
      stars: 4,
      accent: "botany-v2-avatar-two",
    },
    {
      name: "Priya Bennett",
      degree: "Ph.D. in Organisational Studies",
      initials: "PB",
      description:
        "Supports students with case studies, implementation discussions, and recommendation writing where the reasoning still feels too general.",
      completedOrders: 134,
      studentReviews: 57,
      stars: 5,
      accent: "botany-v2-avatar-three",
    },
    {
      name: "Daniel Frost",
      degree: "MBA in Business Development",
      initials: "DF",
      description:
        "Useful for reports and essays that need stronger academic flow, clearer evidence use, and better alignment with the assignment brief.",
      completedOrders: 98,
      studentReviews: 36,
      stars: 4,
      accent: "botany-v2-avatar-four",
    },
  ];
}

function getFeatureItems(page: AssignmentPageData) {
  const source = page.features.length ? page.features : page.outcomes;
  const selected = source.slice(0, 6);

  return selected.map((label, index) => ({
    title: titleCase(label),
    description:
      "This support area helps improve clarity, structure, and alignment with the brief so the final submission feels more focused and more confident.",
    icon:
      [
        IconShieldCheck,
        IconMicroscope,
        IconNotebook,
        IconClockHour4,
        IconFlask2,
        IconLeaf,
      ][index] || IconCheck,
  }));
}

function getReviewCards(page: AssignmentPageData): ReviewCard[] {
  const keyword = getPageKeyword(page);

  return [
    {
      title: page.category.split(",")[0] || "Assignment Help",
      meta: `${keyword} guidance, university student`,
      quote:
        "The support helped me organise the assignment more clearly and finally understand which points deserved the most attention.",
      date: "14 Oct 2025",
    },
    {
      title: "Coursework",
      meta: `${keyword} draft review, university student`,
      quote:
        "The feedback was practical and easy to use. It improved the structure, examples, and overall flow without making the draft feel overcomplicated.",
      date: "06 Oct 2025",
    },
    {
      title: "Case Study",
      meta: `${keyword} submission support, university student`,
      quote:
        "Clear comments, stronger organisation, and much better alignment with the brief. The final version felt more confident before submission.",
      date: "28 Sep 2025",
    },
  ];
}

function getServiceTopics(page: AssignmentPageData) {
  return page.topics.slice(0, 10);
}

function getFreeSupportItems(page: AssignmentPageData) {
  const genericItems = [
    "Structure feedback",
    "Referencing guidance",
    "Formatting review",
    "Draft improvement notes",
    "Introduction and conclusion checks",
    "Submission-readiness support",
  ];

  return page.features.slice(0, 3).concat(genericItems).slice(0, 6);
}

function buildContentSections(page: AssignmentPageData): ContentSection[] {
  if (page.studyGuideSections?.length) {
    return page.studyGuideSections
      .map((section) => ({
        title: section.title,
        paragraphs: section.paragraphs.slice(0, 2),
        bullets: (section.bullets || []).slice(0, 5),
      }))
      .slice(0, 12);
  }

  const keyword = getPageKeyword(page);
  const assignmentTypes = page.assignmentTypes.slice(0, 5);
  const topicSliceOne = page.topics.slice(0, 3);
  const topicSliceTwo = page.topics.slice(3, 6);

  const sections: ContentSection[] = [
    {
      title: `What ${page.title} Usually Includes`,
      paragraphs: [page.whatIs, page.intro],
      bullets: topicSliceOne.length ? topicSliceOne : page.features.slice(0, 3),
    },
    {
      title: `Common Questions Students Have Before Submission`,
      paragraphs: [
        page.commonQuestions,
        `Students usually need support with clearer planning, stronger structure, and better explanation so the final ${keyword.toLowerCase()} submission responds more directly to the brief.`,
      ],
      bullets: topicSliceTwo.length ? topicSliceTwo : page.outcomes.slice(0, 3),
    },
    {
      title: `How SubjectBuddy Helps With ${page.title}`,
      paragraphs: [
        page.whyChoose,
        `Support can focus on ${assignmentTypes.join(", ").toLowerCase()} depending on the brief, deadline, and the parts of the assignment that need the most improvement.`,
      ],
      bullets: page.features.slice(0, 3),
    },
    {
      title: `Assignment Types and Support Areas`,
      paragraphs: [
        `Students commonly request support with ${assignmentTypes.join(", ").toLowerCase()}. The exact structure depends on the question, module expectations, and how much of the draft is already complete.`,
        "The strongest support path is usually the one that makes the brief clearer first, then improves the organisation, evidence use, and academic presentation before submission.",
      ],
      bullets: page.assignmentTypes.slice(0, 3),
    },
    {
      title: `What Students Usually Want to Improve`,
      paragraphs: [
        `Most students using ${page.title.toLowerCase()} are trying to improve a mix of understanding, organisation, recommendation quality, and final submission confidence.`,
        "That usually means deciding what matters most in the brief, choosing the strongest structure, and making sure the argument stays connected from introduction to conclusion.",
      ],
      bullets: page.outcomes.slice(0, 3),
    },
  ];

  (page.studyGuideSections || []).forEach((section) => {
    sections.push({
      title: section.title,
      paragraphs: section.paragraphs.slice(0, 2),
      bullets: (section.bullets || []).slice(0, 5),
    });
  });

  return sections.slice(0, 9);
}

function buildSampleSection(page: AssignmentPageData) {
  const firstGuide = page.studyGuideSections?.[0];
  const keyword = getPageKeyword(page);

  if (firstGuide) {
    return {
      title: `Sample Academic Direction for ${page.title}`,
      heading: firstGuide.title,
      copy: firstGuide.paragraphs.join(" "),
    };
  }

  return {
    title: `Sample Academic Direction for ${page.title}`,
    heading: `How to approach a stronger ${keyword.toLowerCase()} answer`,
    copy: "A strong assignment answer should not just repeat definitions. It should explain the key issue, choose a structure that fits the brief, support the discussion with relevant evidence, and end with a conclusion that clearly responds to the question. This usually creates a more confident academic submission than a draft that only describes the topic in general terms.",
  };
}

function FluidMechanicsAssignmentHelpPage() {
  const pageSlug = "fluid-mechanics-assignment-help";
  const theme = useMantineTheme();
  const desktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const { hash } = useLocation();
  const page = getAssignmentPageBySlug(pageSlug);

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(hash.replace(/^#/, ""));

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });

      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [hash]);

  if (!page) {
    return null;
  }

  const heroTitle = getHeroTitle(page);
  const urgentHeading = getUrgentHeading(page);
  const whatsappUrl = buildWhatsAppUrl(getWhatsAppMessage(page));
  const reviewSummary = getReviewSummary();
  const relatedReviews = getReviewsByAssignmentSlug(page.slug, 3);
  const fallbackReviews = getAllReviews().slice(0, 3);
  const orderPagePath = `/order-form?from=${page.slug}`;
  const scholarCards = getScholarCards(page);
  const featureItems = getFeatureItems(page);
  const reviewCards = getReviewCards(page);
  const serviceTopics = getServiceTopics(page);
  const freeSupportItems = getFreeSupportItems(page);
  const contentSections = buildContentSections(page);
  const sampleSection = buildSampleSection(page);
  const internalLinks = page.internalLinks || [];
  const ctaSection = page.ctaSection;
  const processDescriptions = [
    page.supportSteps[0] ||
      "Share the assignment brief, deadline, rubric, and the areas where you feel stuck.",
    page.supportSteps[1] ||
      "We review the task requirements and identify the most useful support direction.",
    page.supportSteps[3] ||
      page.supportSteps[2] ||
      "You use that support to build a clearer and more confident final submission.",
  ];

  const processSteps = [
    {
      title: "Submit Your Requirements",
      description: processDescriptions[0],
      icon: IconNotebook,
    },
    {
      title: "Secure Payment",
      description: processDescriptions[1],
      icon: IconShieldCheck,
    },
    {
      title: "Receive Your Order",
      description: processDescriptions[2],
      icon: IconFileText,
    },
  ];

  return (
    <main className="botany-v2-page">
      <Seo
        title={page.titleTag || page.metaTitle}
        description={page.metaDescription}
        ogTitle={page.metaTitle}
        path={getAssignmentPagePath(page.slug)}
        schema={[
          createOrganizationSchema(),
          createFaqSchema(page.faqs),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: page.title, path: getAssignmentPagePath(page.slug) },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.title,
            provider: {
              "@type": "Organization",
              name: process.env.REACT_APP_NAME || "SubjectBuddy",
            },
            serviceType: page.title,
            areaServed: [
              "United States",
              "United Kingdom",
              "Canada",
              "Australia",
            ],
            description: page.metaDescription,
          },
          ...(page.schemaTeaches?.length
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "EducationalResource",
                  name: page.title,
                  description: page.metaDescription,
                  url: `https://www.subjectbuddy.com${getAssignmentPagePath(page.slug)}`,
                  educationalUse: "Assignment support",
                  learningResourceType: "Subject help guide",
                  teaches: page.schemaTeaches,
                  provider: {
                    "@type": "Organization",
                    name: "SubjectBuddy",
                    url: "https://www.subjectbuddy.com/",
                  },
                },
              ]
            : []),
        ]}
      />

      <Container size="xl" className="botany-v2-shell">
        <section className="botany-v2-hero">
          <Breadcrumbs
            items={[{ label: "Home", href: "/" }, { label: page.title }]}
          />

          <div className="botany-v2-hero-grid">
            <div className="botany-v2-hero-copy">
              <Title order={1} className="botany-v2-title">
                {heroTitle}
              </Title>
              <Text className="botany-v2-subtitle">{page.intro}</Text>

              <div className="botany-v2-trust">
                Trusted by <strong>{reviewSummary.totalReviews}+</strong>{" "}
                students seeking clearer assignment support
              </div>

              <div className="botany-v2-trust-row">
                {[
                  {
                    label: "Assignment Help",
                    score: "4.9",
                    icon: IconShieldCheck,
                  },
                  {
                    label: "Student Rated Support",
                    score: "4.8",
                    icon: IconUserCheck,
                  },
                  {
                    label: "Verified Review Signals",
                    score: "4.9",
                    icon: IconRosetteDiscountCheck,
                  },
                ].map((item) => (
                  <div key={item.label} className="botany-v2-trust-card">
                    <div className="botany-v2-trust-head">
                      <ThemeIcon
                        size={34}
                        radius="xl"
                        color="orange"
                        variant="light"
                      >
                        <item.icon size={18} />
                      </ThemeIcon>
                      <div>
                        <div className="botany-v2-trust-label">
                          {item.label}
                        </div>
                        <div className="botany-v2-stars">
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <IconStarFilled size={14} />
                          <span>{item.score}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="botany-v2-form-shell">
              <Paper
                className="botany-v2-form-card"
                radius="xl"
                p={desktop ? "1.5rem" : "1rem"}
              >
                <Stack gap="md">
                  <Title order={3} ta="center" className="botany-v2-form-title">
                    Struggling with your assignment?
                  </Title>
                  <div className="botany-v2-form-badges">
                    <span>
                      <IconCheck size={14} /> Clearer structure
                    </span>
                    <span>
                      <IconCheck size={14} /> Better topic explanation
                    </span>
                    <span>
                      <IconCheck size={14} /> Faster turnaround
                    </span>
                  </div>
                  <GetHelpForm
                    formSize="sm"
                    flatForm
                    headerMode="minimal"
                    submitLabel={`Get ${getPageKeyword(page)} Help`}
                  />
                </Stack>
              </Paper>
            </div>
          </div>
        </section>

        <section className="botany-v2-experts">
          <div className="botany-v2-experts-shell">
            <div className="botany-v2-section-heading botany-v2-section-heading-center">
              <Title order={2}>Meet the Experts Supporting {page.title}</Title>
              <Text>
                Our team includes real subject-focused mentors who know how to
                make the brief clearer, the structure stronger, and the final
                submission more confident.
              </Text>
            </div>

            <SimpleGrid cols={{ base: 1, md: 2, xl: 4 }} spacing="lg">
              {scholarCards.map((card) => (
                <Paper
                  key={card.name}
                  className="botany-v2-scholar-card"
                  radius="xl"
                  p="lg"
                >
                  <Stack gap="md" h="100%">
                    <Group align="flex-start" wrap="nowrap">
                      <div className={`botany-v2-avatar ${card.accent}`}>
                        <span>{card.initials}</span>
                        <span className="botany-v2-avatar-status" />
                      </div>
                      <div>
                        <Text fw={900}>{card.name}</Text>
                        <Text size="sm" c="dimmed">
                          {card.degree}
                        </Text>
                        <div className="botany-v2-scholar-stars">
                          {Array.from({ length: 5 }).map((_, index) => (
                            <IconStarFilled
                              key={`${card.name}-${index}`}
                              size={14}
                              className={
                                index < card.stars
                                  ? "botany-v2-star-on"
                                  : "botany-v2-star-off"
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </Group>

                    <Text size="sm" style={{ lineHeight: 1.8 }}>
                      {card.description}
                    </Text>

                    <div className="botany-v2-scholar-footer">
                      <div className="botany-v2-scholar-stats">
                        <span>
                          <IconFileText size={13} />
                          <strong>{card.completedOrders}</strong> Completed
                          Orders
                        </span>
                        <span>
                          <IconMessageCircle size={13} />
                          <strong>{card.studentReviews}</strong> Student Reviews
                        </span>
                      </div>

                      <Button
                        component="a"
                        href={whatsappUrl}
                        radius="md"
                        variant="outline"
                        color="green"
                      >
                        Get Help
                      </Button>
                    </div>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </div>
        </section>

        <section className="botany-v2-process">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>
              From Brief to Better Submission: How Support Works
            </Title>
          </div>

          <div className="botany-v2-process-flow">
            {processSteps.map((step, index) => (
              <div key={step.title} className="botany-v2-process-node">
                <div className="botany-v2-process-item">
                  <div className="botany-v2-process-icon-shell">
                    <step.icon size={56} stroke={1.5} />
                  </div>
                  <Title order={3}>{step.title}</Title>
                  <Text c="dimmed" size="sm" style={{ lineHeight: 1.8 }}>
                    {step.description}
                  </Text>
                </div>

                {index < processSteps.length - 1 ? (
                  <div className="botany-v2-process-arrow" aria-hidden="true">
                    <IconArrowRight size={44} stroke={2.2} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="botany-v2-process-cta">
            <Button
              component={Link}
              to={orderPagePath}
              color="green"
              radius="md"
              size="md"
            >
              Order Now
            </Button>
          </div>
        </section>

        <section className="botany-v2-features">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>Why Students Choose {page.title}</Title>
            <Text>
              We focus on clearer explanations, stronger structure, and more
              useful academic direction before submission.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="xl">
            {featureItems.map((item) => (
              <div key={item.title} className="botany-v2-feature-row">
                <ThemeIcon
                  size={64}
                  radius="xl"
                  color="green"
                  variant="light"
                  className="botany-v2-feature-icon"
                >
                  <item.icon size={28} />
                </ThemeIcon>
                <Stack gap={6} className="botany-v2-feature-copy">
                  <Title order={3}>{item.title}</Title>
                  <Text c="dimmed" size="sm" style={{ lineHeight: 1.8 }}>
                    {item.description}
                  </Text>
                </Stack>
              </div>
            ))}
          </SimpleGrid>

          <div className="botany-v2-feature-cta">
            <Button
              component={Link}
              to={orderPagePath}
              radius="md"
              color="green"
              size="md"
            >
              Order Now
            </Button>
          </div>
        </section>

        <section className="botany-v2-reviews">
          <div className="botany-v2-section-heading botany-v2-section-heading-center">
            <Title order={2}>What Students Say About {page.title}</Title>
            <Text>
              Reviews from students who wanted clearer structure, faster
              direction, and stronger final submissions.
            </Text>
          </div>

          <div className="botany-v2-review-platforms">
            {[
              { label: "Assignment Help", icon: IconShieldCheck },
              { label: "Student Rated Support", icon: IconUserCheck },
              {
                label: "Verified Review Signals",
                icon: IconRosetteDiscountCheck,
              },
            ].map((item) => (
              <div key={item.label} className="botany-v2-platform-pill">
                <item.icon size={16} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {reviewCards.map((review) => (
              <Paper
                key={review.title}
                className="botany-v2-review-card"
                radius="lg"
                p="lg"
              >
                <Stack gap="sm">
                  <div className="botany-v2-stars">
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                    <IconStarFilled size={14} />
                  </div>
                  <Text fw={800}>{review.title}</Text>
                  <Text size="xs" c="dimmed">
                    {review.meta}
                  </Text>
                  <Text size="sm" c="dimmed" style={{ lineHeight: 1.8 }}>
                    {review.quote}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {review.date}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </section>

        <section className="botany-v2-urgent">
          <Paper
            className="botany-v2-urgent-card"
            radius="xl"
            p={desktop ? "2rem" : "1.25rem"}
          >
            <Grid align="center" gutter="xl">
              <Grid.Col span={{ base: 12, md: 4 }}>
                <div className="botany-v2-urgent-visual">
                  <IconNotebook size={84} />
                  <IconMicroscope
                    size={34}
                    className="botany-v2-float botany-v2-float-one"
                  />
                  <IconLeaf
                    size={30}
                    className="botany-v2-float botany-v2-float-two"
                  />
                  <IconFlask2
                    size={30}
                    className="botany-v2-float botany-v2-float-three"
                  />
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  <Title order={2}>{urgentHeading}</Title>
                  <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                    Access urgent support for drafts, reports, case studies, and
                    coursework improvement when the deadline is close and the
                    next steps need to become clearer quickly.
                  </Text>
                  <Button
                    component={Link}
                    to={orderPagePath}
                    radius="md"
                    color="green"
                    leftSection={<IconClockHour4 size={18} />}
                    className="botany-v2-urgent-button"
                  >
                    Order Support
                  </Button>
                </Stack>
              </Grid.Col>
            </Grid>
          </Paper>
        </section>

        <section className="botany-v2-services">
          <div className="botany-v2-services-grid">
            <div className="botany-v2-services-left">
              <Title order={2}>
                {page.title} Covers Multiple Support Areas
              </Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                SubjectBuddy supports assignments that need clearer structure,
                better topic explanation, stronger evidence use, and more
                confident academic flow before submission.
              </Text>
              <Text fw={800}>Type of support</Text>
              <ul className="botany-v2-service-list">
                {serviceTopics.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="botany-v2-services-right">
              <Paper className="botany-v2-freebies-card" radius="xl" p="lg">
                <Stack gap="md">
                  <Text fw={900} tt="uppercase" size="sm" c="orange">
                    Included support areas
                  </Text>
                  {freeSupportItems.map((item) => (
                    <div key={item} className="botany-v2-freebie-row">
                      <span className="botany-v2-freebie-label">
                        <IconCheck size={16} />
                        {item}
                      </span>
                      <span className="botany-v2-freebie-value">Included</span>
                    </div>
                  ))}
                  <Button
                    component="a"
                    href={whatsappUrl}
                    radius="md"
                    color="orange"
                  >
                    Get Support
                  </Button>
                </Stack>
              </Paper>
            </div>
          </div>
        </section>

        <section className="botany-v2-content">
          <div className="botany-v2-content-scroll">
            <div className="botany-v2-content-grid">
              {contentSections.map((section) => (
                <article key={section.title} className="botany-v2-article-card">
                  <Stack gap="md">
                    <Title order={2}>{section.title}</Title>
                    {section.paragraphs.map((paragraph) => (
                      <Text
                        key={paragraph}
                        c="dimmed"
                        style={{ lineHeight: 1.9 }}
                      >
                        {paragraph}
                      </Text>
                    ))}
                    <ul className="botany-v2-article-list">
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  </Stack>
                </article>
              ))}

              <article className="botany-v2-article-card">
                <Stack gap="md">
                  <Title order={2}>{sampleSection.title}</Title>
                  <Paper className="botany-v2-sample-card" radius="lg" p="lg">
                    <Stack gap="sm">
                      <Title order={3}>{sampleSection.heading}</Title>
                      <Text c="dimmed" style={{ lineHeight: 1.85 }}>
                        {sampleSection.copy}
                      </Text>
                    </Stack>
                  </Paper>
                </Stack>
              </article>

              {internalLinks.length ? (
                <article className="botany-v2-article-card">
                  <Stack gap="md">
                    <Title order={2}>Related Support and Study Guides</Title>
                    <Text c="dimmed" style={{ lineHeight: 1.9 }}>
                      If your brief overlaps with nearby topics, you can also
                      continue with{" "}
                      {internalLinks.map((link, index) => (
                        <span key={link.href}>
                          {index > 0
                            ? index === internalLinks.length - 1
                              ? " and "
                              : ", "
                            : ""}
                          <Link to={link.href}>{link.label}</Link>
                        </span>
                      ))}
                      .
                    </Text>
                  </Stack>
                </article>
              ) : null}
            </div>
          </div>
        </section>

        {ctaSection ? (
          <section className="botany-v2-urgent">
            <Paper
              className="botany-v2-urgent-card"
              radius="xl"
              p={desktop ? "2rem" : "1.25rem"}
            >
              <Grid align="center" gutter="xl">
                <Grid.Col span={{ base: 12, md: 8 }}>
                  <Stack gap="md">
                    <Title order={2}>{ctaSection.heading}</Title>
                    <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                      {ctaSection.body}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Button
                    component={Link}
                    to={ctaSection.buttonUrl}
                    radius="md"
                    color="green"
                    size="md"
                    fullWidth
                  >
                    {ctaSection.buttonText}
                  </Button>
                </Grid.Col>
              </Grid>
            </Paper>
          </section>
        ) : null}

        <section className="botany-v2-live-reviews">
          <ReviewsPreviewSection
            title={`Recent Student Feedback on ${page.title}`}
            description="Selected student feedback that reflects clearer structure, better explanations, and calmer deadline support."
            reviews={relatedReviews.length ? relatedReviews : fallbackReviews}
            compactCards
            plainTrustBar
          />
        </section>

        <section className="botany-v2-faqs">
          <div className="botany-v2-section-heading">
            <Title order={2}>FAQs on {page.title}</Title>
          </div>
          <AssignmentFAQ faqs={page.faqs} />
        </section>
      </Container>
    </main>
  );
}

export default FluidMechanicsAssignmentHelpPage;
